import { PasswordSchema } from '@comficonf/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Circle } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import { inlineLinkClasses } from '@/components/page';
import { Callout } from '@/components/ui/callout';
import { describedBy, FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { isApiError } from '@/lib/api/client';
import { api } from '@/lib/api/instance';
import { queryKeys } from '@/lib/api/keys';
import { safeNextPath, useDocumentTitle } from '@/lib/hooks';
import { cn } from '@/lib/utils';
import { AuthLayout } from './auth-layout';
import { SubmitButton, useFocusAfterRender } from './form-submit';

export const MIN_PASSWORD_LENGTH = PasswordSchema.minLength ?? 10;
const MAX_PASSWORD_LENGTH = PasswordSchema.maxLength ?? 256;

const RegisterFormSchema = z
  .object({
    email: z
      .string()
      .trim()
      .min(1, 'Enter your email address.')
      .pipe(z.email('Enter a valid email address.').max(320, 'Use at most 320 characters.')),
    password: z
      .string()
      .min(MIN_PASSWORD_LENGTH, `Use at least ${MIN_PASSWORD_LENGTH} characters.`)
      .max(MAX_PASSWORD_LENGTH, `Use at most ${MAX_PASSWORD_LENGTH} characters.`),
    confirm: z.string(),
  })
  .refine((values) => values.confirm === values.password, {
    path: ['confirm'],
    message: 'The passwords do not match.',
  });
type RegisterForm = z.input<typeof RegisterFormSchema>;

export function PasswordRule({ password, id }: { password: string; id: string }) {
  const met = password.length >= MIN_PASSWORD_LENGTH;
  return (
    <p
      id={id}
      className={cn(
        'flex items-center gap-1.5 text-xs',
        met ? 'text-success-soft-fg' : 'text-muted',
      )}
    >
      {met ? <Check className="size-3.5" aria-hidden /> : <Circle className="size-3" aria-hidden />}
      At least {MIN_PASSWORD_LENGTH} characters
      <span className="sr-only">{met ? '(met)' : '(not met yet)'}</span>
    </p>
  );
}

export function RegisterPage() {
  useDocumentTitle('Create account');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNextPath(params.get('next'));
  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<RegisterForm>({
    resolver: zodResolver(RegisterFormSchema),
    defaultValues: { email: '', password: '', confirm: '' },
  });
  const password = useWatch({ control, name: 'password' }) ?? '';
  const [rootErrorRef, focusRootError] = useFocusAfterRender<HTMLDivElement>();

  const onSubmit = handleSubmit(async (values) => {
    try {
      const session = await api.register({ email: values.email.trim(), password: values.password });
      queryClient.setQueryData(queryKeys.session, session);
      navigate(next ?? '/', { replace: true });
    } catch (error) {
      if (isApiError(error, 'EMAIL_TAKEN')) {
        setError(
          'email',
          { message: 'An account with this email already exists. Try signing in.' },
          { shouldFocus: true },
        );
      } else if (isApiError(error, 'REGISTRATION_DISABLED')) {
        focusRootError();
        setError('root', {
          message: 'New accounts are not being accepted on this ComfiConf instance.',
        });
      } else if (isApiError(error, 'VALIDATION_FAILED') && error.fieldErrors.length > 0) {
        let focused = false;
        for (const fieldError of error.fieldErrors) {
          const name = fieldError.path.endsWith('password')
            ? 'password'
            : fieldError.path.endsWith('email')
              ? 'email'
              : 'root';
          if (name === 'root') {
            if (!focused) focusRootError();
            setError(name, { message: fieldError.message });
          } else {
            setError(name, { message: fieldError.message }, { shouldFocus: !focused });
          }
          focused = true;
        }
      } else {
        focusRootError();
        setError('root', {
          message: error instanceof Error ? error.message : 'Creating the account failed.',
        });
      }
    }
  });

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Pair your first server in a couple of minutes."
      footer={
        <>
          Already have an account?{' '}
          <Link
            to={next ? `/login?next=${encodeURIComponent(next)}` : '/login'}
            className={inlineLinkClasses}
          >
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-5">
        {errors.root ? (
          <Callout tone="danger" role="alert" ref={rootErrorRef} tabIndex={-1}>
            {errors.root.message}
          </Callout>
        ) : null}
        <FormField id="register-email" label="Email" error={errors.email?.message}>
          <Input
            id="register-email"
            type="email"
            autoComplete="email"
            autoFocus
            aria-invalid={errors.email ? true : undefined}
            aria-describedby={describedBy('register-email', { error: !!errors.email })}
            {...register('email')}
          />
        </FormField>
        <FormField
          id="register-password"
          label="Password"
          error={errors.password?.message}
          hint={<PasswordRule id="register-password-rule" password={password} />}
        >
          <Input
            id="register-password"
            type="password"
            autoComplete="new-password"
            aria-invalid={errors.password ? true : undefined}
            aria-describedby={describedBy('register-password', {
              hint: true,
              error: !!errors.password,
            })}
            {...register('password')}
          />
        </FormField>
        <FormField id="register-confirm" label="Confirm password" error={errors.confirm?.message}>
          <Input
            id="register-confirm"
            type="password"
            autoComplete="new-password"
            aria-invalid={errors.confirm ? true : undefined}
            aria-describedby={describedBy('register-confirm', { error: !!errors.confirm })}
            {...register('confirm')}
          />
        </FormField>
        <SubmitButton variant="primary" size="lg" className="w-full" submitting={isSubmitting}>
          Create account
        </SubmitButton>
      </form>
    </AuthLayout>
  );
}
